import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import type { components } from '@fluencelabs/vodopad-admin-api'
import { useTable } from '@refinedev/antd'
import { useCreate, useDelete, useInvalidate } from '@refinedev/core'
import {
  Breadcrumb,
  Button,
  Form,
  Input,
  message,
  Modal,
  Table,
  Typography,
} from 'antd'
import { useState } from 'react'

import { FormattedDate } from '@/components/common/FormattedDate'
import { TruncatedId } from '@/components/common/TruncatedId'
import { getPersistedPageSize, tablePaginationConfig } from '@/types/api'

type InviteDto =
  components['schemas']['PaginatedResponse_InviteDto']['items'][number]

export default function InvitationsListPage() {
  const [createOpen, setCreateOpen] = useState(false)
  const [form] = Form.useForm()
  const { mutate: create } = useCreate()
  const { mutate: remove } = useDelete()
  const invalidate = useInvalidate()

  const { tableProps } = useTable<InviteDto>({
    resource: 'invitations',
    pagination: { pageSize: getPersistedPageSize() },
    syncWithLocation: true,
  })

  const handleCreate = async () => {
    try {
      const values = await form.validateFields()
      create(
        { resource: 'invitations', values: { email: values.email } },
        {
          onSuccess: () => {
            form.resetFields()
            setCreateOpen(false)
            void invalidate({ resource: 'invitations', invalidates: ['list'] })
            void message.success('Invitation created')
          },
        },
      )
    } catch {
      // validation error
    }
  }

  const handleRevoke = (email: string) => {
    Modal.confirm({
      title: 'Revoke Invitation',
      content: `Are you sure you want to revoke the invitation for ${email}?`,
      okText: 'Revoke',
      okType: 'danger',
      onOk: () => {
        remove(
          { resource: 'invitations', id: email, meta: { email } },
          {
            onSuccess: () => {
              void invalidate({
                resource: 'invitations',
                invalidates: ['list'],
              })
              void message.success('Invitation revoked')
            },
          },
        )
      },
    })
  }

  return (
    <div>
      <Breadcrumb
        style={{ marginBottom: 16 }}
        items={[{ title: 'Invitations' }]}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setCreateOpen(true)}
        >
          Create Invitation
        </Button>
      </div>

      <Table
        {...tableProps}
        pagination={tablePaginationConfig(tableProps)}
        rowKey="email"
        columns={[
          {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            render: (email: string) => (
              <Typography.Text copyable={{ text: email }}>
                {email}
              </Typography.Text>
            ),
          },
          {
            title: 'User',
            dataIndex: 'userId',
            key: 'userId',
            width: 160,
            render: (id: string | null) => (id ? <TruncatedId id={id} /> : '—'),
          },
          {
            title: 'Created',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 150,
            render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
          },
          {
            title: 'Updated',
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            width: 150,
            render: (d: string) => <FormattedDate date={d} fmt="PPp" />,
          },
          {
            title: '',
            key: 'actions',
            width: 50,
            render: (_: unknown, record: InviteDto) => (
              <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                disabled={
                  record.removedAt !== null && record.removedAt !== undefined
                }
                onClick={() => handleRevoke(record.email)}
              />
            ),
          },
        ]}
        scroll={{ x: 700 }}
      />

      <Modal
        title="Create Invitation"
        open={createOpen}
        onOk={() => void handleCreate()}
        okText="Create Invitation"
        onCancel={() => setCreateOpen(false)}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Email is required' },
              { type: 'email', message: 'Invalid email' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
